import React, { useContext, useEffect, useRef, useState } from "react";
import { Control, Controller, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { FaDownload, FaMinusCircle } from "react-icons/fa";
import { InputErrorMessage } from "./InputErrorMessage";
import { CustomLabel } from "./CustomLabel";
import { FileToBase64 } from "shared/util/functions/file/FileFunctions";
import { UserContext } from "context/UserContext";
import { downloadFile } from "shared/util/functions/download/DownloadFunctions";
import toast from "react-hot-toast";
import { Tooltip } from "@material-ui/core";
import showAlert from "shared/util/functions/alert/showAlert";
import { mensagemPadraoExclusao } from "shared/util/functions/alert/MensagensPadrõesAlerta";

export function formatarDataFromDateHora(date: any) {
    const [data, hora] = new Date(date).toLocaleString().split(", ");
    return `${data} - ${hora}`;
}

export interface AnexoItem {
    id?: string;
    idAdd?: string;
    nome: string;
    anexadoPor?: string;
    data?: string;
    origemLancamento?: "CONTAS_PAGAR" | "GED" | "CONCILIACAO_BANCARIA" | "GROUP_PAY";
    tipoDocumento?: "DOC_PAGAMENTO" | "DOC_FISCAL" | "OUTROS";
    anexoComprovante?: boolean;
    arquivo?: string;
    mimeType?: string;
}

type InputMultiAnexosProps = {
    name: string;
    control: Control<any>;
    setValue: UseFormSetValue<any>;
    watch: UseFormWatch<any>;
    label?: React.ReactNode;
    required?: boolean;
    isVisualizacao?: boolean;
    accept?: string;
    baixarAnexo?: (anexo: AnexoItem) => void;
    onAnexoChange?: (anexos: AnexoItem[]) => void;
};

const InputMultiAnexos = ({
    name,
    control,
    setValue,
    watch,
    label,
    required = false,
    isVisualizacao = false,
    accept = ".pdf,.doc,.xls,.png,.jpeg,.csv",
    baixarAnexo,
    onAnexoChange,
}: InputMultiAnexosProps) => {
    const { usuarioLogado } = useContext(UserContext);
    const [isArrastando, setIsArrastando] = useState(false);
    const [renderKey, setRenderKey] = useState<string>(`anexo-${Math.random()}`);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const anexos: AnexoItem[] = watch(name) || [];

    useEffect(() => {
        setRenderKey(`anexo-${Math.random()}`);
    }, []);

    const translateLabel = (option: string): string => {
        const labels: Record<string, string> = {
            CONTAS_PAGAR: "Contas a Pagar",
            GED: "GED",
            CONCILIACAO_BANCARIA: "Conciliação bancária",
            GROUP_PAY: "Group Pay",
        };
        return labels[option] || option;
    };

    const validarExtensaoArquivo = (file: string) => {
        if (accept && accept !== "") {
            const indiceUltimoPonto = file.lastIndexOf(".");
            if (indiceUltimoPonto === -1) {
                return false;
            }
            const extensoesPermitidas = accept.split(",").map((ext) => ext.trim().toLowerCase());
            const extensaoArquivo = file.substring(file.lastIndexOf(".")).toLowerCase();
            return extensoesPermitidas.includes(extensaoArquivo);
        }
        return true;
    }

    const exbirMensagemErroExtensao = () => {
        const formatosArray = accept.replace(/\./g, "").toUpperCase().split(",");
        let formatosPermitidos = "";

        if (formatosArray.length > 1) {
            const todosMenosOUltimo = formatosArray.slice(0, -1).join(", ");
            const ultimoItem = formatosArray.slice(-1)[0];
            formatosPermitidos = `${todosMenosOUltimo} e ${ultimoItem}`;
        } else {
            formatosPermitidos = formatosArray[0] || "";
        }
        toast.error(`É permitido anexar somente arquivos nos formatos: ${formatosPermitidos}`)
        if (inputRef.current) inputRef.current.value = "";
        return;
    }

    const addAnexo = async (file: File) => {
        if (!file) return;
        if (!validarExtensaoArquivo(file.name)) {
            exbirMensagemErroExtensao();
            return;
        }
        try {
            const base64 = await FileToBase64(file);
            const novoAnexo: AnexoItem = {
                idAdd: `temp-${Date.now()}-${Math.random()}`,
                nome: file.name,
                anexadoPor: usuarioLogado.nome,
                data: new Date().toISOString(),
                origemLancamento: "GROUP_PAY",
                tipoDocumento: "OUTROS",
                anexoComprovante: false,
                arquivo: base64,
                mimeType: file.type,
            };

            const novosAnexos = [...anexos, novoAnexo];
            setValue(name, novosAnexos, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true
            });

            onAnexoChange?.(novosAnexos);
        } catch (error) {
            console.error("Erro ao adicionar anexo:", error);
        }
    };

    const removeAnexo = (anexo: AnexoItem) => {
        let novosAnexos: AnexoItem[];

        if (anexo.id) {
            novosAnexos = anexos.filter(a => a.id !== anexo.id);
        } else {
            novosAnexos = anexos.filter(a => a.idAdd !== anexo.idAdd);
        }

        setValue(name, novosAnexos, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true
        });

        onAnexoChange?.(novosAnexos);
    };

    const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
        setIsArrastando(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
        setIsArrastando(false);
    };

    const handleDrop = async (event: React.DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
        setIsArrastando(false);

        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            await addAnexo(files[0]);
            event.dataTransfer?.clearData();
        }
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        await addAnexo(file);

        if (inputRef.current) {
            inputRef.current.value = "";
        }
    };

    const handleBaixarAnexo = (anexo: AnexoItem) => {
        if (baixarAnexo) {
            baixarAnexo(anexo);
        } else if (anexo.arquivo) {
            downloadFile(anexo.arquivo, anexo.nome, anexo.mimeType);
        }
    };

    const handleRemoverAnexo = (anexo: AnexoItem) => {
        showAlert({
            texto: mensagemPadraoExclusao(),
            tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
            onConfirm: () => removeAnexo(anexo),
        });
    };

    const renderAnexoItem = (anexo: AnexoItem, index: number) => {
        const showLabel = index === 0;

        return (
            <div key={anexo.id || anexo.idAdd} className="anexo-item" style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "1rem",
                padding: "0.75rem",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                backgroundColor: "#f9f9f9"
            }}>
                <div style={{ flex: "1", minWidth: "200px", overflow: "hidden" }}>
                    {showLabel && (
                        <CustomLabel className="mb-1">
                            Documento
                        </CustomLabel>
                    )}
                    <Tooltip title={anexo.nome} placement="top" arrow PopperProps={{ style: { zIndex: 9999 } }}>
                        <span
                        onClick={() => handleBaixarAnexo(anexo)}
                            style={{
                                display: 'block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                cursor: 'pointer',
                                color: "#0066cc",
                                textDecoration: "underline",
                            }}
                    >
                            <FaDownload style={{ marginRight: "0.5rem", color: "#666", verticalAlign: 'middle' }} />
                            {anexo.nome}
                        </span>
                    </Tooltip>
                </div>

                <div style={{ flex: "1", minWidth: "150px", marginLeft: "1rem" }}>
                    {showLabel && (
                        <CustomLabel className="mb-1">
                            Anexado por
                        </CustomLabel>
                    )}
                    <span>{anexo.anexadoPor || ""}</span>
                </div>

                <div style={{ flex: "1", minWidth: "120px", marginLeft: "1rem" }}>
                    {showLabel && (
                        <CustomLabel className="mb-1">
                            Data
                        </CustomLabel>
                    )}
                    <span>
                        {anexo.data ? formatarDataFromDateHora(anexo.data) : ""}
                    </span>
                </div>

                <div style={{ flex: "1", minWidth: "150px", marginLeft: "1rem" }}>
                    {showLabel && (
                        <CustomLabel className="mb-1">
                            Origem
                        </CustomLabel>
                    )}
                    <span>
                        {anexo.origemLancamento ? translateLabel(anexo.origemLancamento) : ""}
                    </span>
                </div>

                {!isVisualizacao && (
                    <div style={{ marginLeft: "1rem" }}>
                        {showLabel && (
                            <div style={{ height: "1.5rem", marginBottom: "0.25rem" }}></div>
                        )}
                        <button
                            type="button"
                            onClick={() => handleRemoverAnexo(anexo)}
                            style={{
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                                padding: "0.25rem",
                            }}
                            aria-label="Remover anexo"
                        >
                            <FaMinusCircle color="#dc3545" size={18} />
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const anexosFiltrados = anexos.filter(anexo => !anexo.anexoComprovante);

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={[]}
            render={({ fieldState }) => (
                <div style={{ width: "100%" }}>
                    <CustomLabel>
                        {label} {required && <span style={{ color: "red" }}>*</span>}
                    </CustomLabel>

                    {anexosFiltrados.length > 0 && (
                        <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
                            {anexosFiltrados.map((anexo, index) =>
                                renderAnexoItem(anexo, index)
                            )}
                        </div>
                    )}

                    {!isVisualizacao && (
                        <>
                            <input
                                ref={inputRef}
                                type="file"
                                style={{ display: "none" }}
                                accept={accept}
                                onChange={handleFileChange}
                                onClick={(e) => (e.currentTarget.value = "")}
                            />
                            <label
                                htmlFor={`${name}-file-input`}
                                onClick={() => inputRef.current?.click()}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                style={{
                                    display: "block",
                                    width: "100%",
                                    padding: "2rem 1.1rem",
                                    border: isArrastando ? "2px dashed #0066cc" : "1px solid #ddd",
                                    borderRadius: "4px",
                                    backgroundColor: isArrastando ? "#eaf2fa" : "#f5f5f5",
                                    color: "#333",
                                    fontSize: "1.4rem",
                                    textAlign: "center",
                                    cursor: "pointer",
                                    marginTop: "1rem"
                                }}
                            >
                                Arraste os arquivos ou clique para adicioná-los.
                                Formatos permitidos: PDF, DOC, XLS, PNG, JPEG e CSV.
                            </label>
                        </>
                    )}

                    {fieldState.error && (
                        <InputErrorMessage>{fieldState.error.message}</InputErrorMessage>
                    )}
                </div>
            )}
        />
    );
};

export default InputMultiAnexos;